import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AdminLearningContentRow,
  AdminLearningContentRowUpsert,
  AdminLearningContentSubCategory,
} from '../types/domain';
import {
  getWorkspaceLearningContentRows,
  replaceWorkspaceLearningContentRows,
} from '../services/workspaceLearningContentService';
import { loadWorkspaceLearningContentRowsCache } from '../utils/workspaceLearningContentCache';

interface UseWorkspaceLearningContentRowsParams {
  workspaceId: string;
  gradeId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
}

function workspaceLearningRowsScopeId(
  workspaceId: string,
  gradeId: string,
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): string {
  return `${workspaceId}|${gradeId}|${gradeNumber}|${subCategory}|${templateName.trim()}`;
}

export function useWorkspaceLearningContentRows({
  workspaceId,
  gradeId,
  gradeNumber,
  subCategory,
  templateName,
}: UseWorkspaceLearningContentRowsParams) {
  const [rows, setRows] = useState<AdminLearningContentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestFetchTargetRef = useRef('');
  const fetchGenerationRef = useRef(0);
  const rowsOriginScopeIdRef = useRef('');

  const scopeTemplate = templateName.trim();
  const scopeGradeId = gradeId.trim();
  if (workspaceId && scopeGradeId && scopeTemplate) {
    latestFetchTargetRef.current = workspaceLearningRowsScopeId(
      workspaceId,
      scopeGradeId,
      gradeNumber,
      subCategory,
      scopeTemplate
    );
  } else {
    latestFetchTargetRef.current = '';
  }

  const load = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      if (!workspaceId || !scopeGradeId || !scopeTemplate) {
        fetchGenerationRef.current += 1;
        rowsOriginScopeIdRef.current = '';
        setRows([]);
        setLoading(false);
        return [];
      }
      const fetchTarget = workspaceLearningRowsScopeId(
        workspaceId,
        scopeGradeId,
        gradeNumber,
        subCategory,
        scopeTemplate
      );
      const fetchId = ++fetchGenerationRef.current;
      const forceRefresh = options?.forceRefresh === true;
      setLoading(true);
      setError(null);
      try {
        const loaded = await getWorkspaceLearningContentRows(
          workspaceId,
          scopeGradeId,
          gradeNumber,
          subCategory,
          scopeTemplate,
          forceRefresh ? { forceRefresh: true } : undefined
        );
        if (fetchId !== fetchGenerationRef.current) return loaded;
        if (latestFetchTargetRef.current !== fetchTarget) return loaded;
        rowsOriginScopeIdRef.current = fetchTarget;
        setRows(loaded);
        return loaded;
      } catch (e) {
        if (fetchId !== fetchGenerationRef.current) return [];
        if (latestFetchTargetRef.current !== fetchTarget) return [];
        rowsOriginScopeIdRef.current = '';
        setError(e instanceof Error ? e.message : '학습내용 데이터를 불러오지 못했습니다.');
        setRows([]);
        return [];
      } finally {
        if (fetchId === fetchGenerationRef.current && latestFetchTargetRef.current === fetchTarget) {
          setLoading(false);
        }
      }
    },
    [gradeNumber, workspaceId, scopeGradeId, subCategory, scopeTemplate]
  );

  useLayoutEffect(() => {
    if (!workspaceId || !scopeGradeId || !scopeTemplate) {
      fetchGenerationRef.current += 1;
      rowsOriginScopeIdRef.current = '';
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchTarget = workspaceLearningRowsScopeId(
      workspaceId,
      scopeGradeId,
      gradeNumber,
      subCategory,
      scopeTemplate
    );
    const fetchId = ++fetchGenerationRef.current;

    const cached = loadWorkspaceLearningContentRowsCache(
      workspaceId,
      scopeGradeId,
      gradeNumber,
      subCategory,
      scopeTemplate
    );
    const hasCache = cached.found;
    if (hasCache) {
      // 캐시를 즉시 표시하되, getWorkspaceLearningContentRows의 head(count:max(updated_at)) 비교로
      // 아래에서 백그라운드 재검증한다(원격 변경을 영구히 놓치지 않기 위함).
      rowsOriginScopeIdRef.current = fetchTarget;
      setRows(cached.rows);
      setError(null);
      setLoading(false);
    } else {
      rowsOriginScopeIdRef.current = '';
      setRows([]);
      setLoading(true);
      setError(null);
    }

    void (async () => {
      try {
        const loaded = await getWorkspaceLearningContentRows(
          workspaceId,
          scopeGradeId,
          gradeNumber,
          subCategory,
          scopeTemplate
        );
        if (fetchId !== fetchGenerationRef.current) return;
        if (latestFetchTargetRef.current !== fetchTarget) return;
        rowsOriginScopeIdRef.current = fetchTarget;
        setRows(loaded);
      } catch (e) {
        if (fetchId !== fetchGenerationRef.current) return;
        if (latestFetchTargetRef.current !== fetchTarget) return;
        if (!hasCache) {
          rowsOriginScopeIdRef.current = '';
          setError(e instanceof Error ? e.message : '학습내용 데이터를 불러오지 못했습니다.');
          setRows([]);
        }
      } finally {
        if (fetchId === fetchGenerationRef.current && latestFetchTargetRef.current === fetchTarget) {
          setLoading(false);
        }
      }
    })();

    return () => {
      fetchGenerationRef.current += 1;
    };
  }, [workspaceId, scopeGradeId, scopeTemplate, gradeNumber, subCategory]);

  const saveRows = useCallback(
    async (nextRows: AdminLearningContentRowUpsert[]) => {
      if (!workspaceId || !scopeGradeId || !scopeTemplate) return [];
      setSaving(true);
      setError(null);
      try {
        const saved = await replaceWorkspaceLearningContentRows(
          workspaceId,
          scopeGradeId,
          gradeNumber,
          subCategory,
          scopeTemplate,
          nextRows
        );
        const scopeId = workspaceLearningRowsScopeId(
          workspaceId,
          scopeGradeId,
          gradeNumber,
          subCategory,
          scopeTemplate
        );
        if (latestFetchTargetRef.current === scopeId) {
          rowsOriginScopeIdRef.current = scopeId;
          setRows(saved);
        }
        return saved;
      } catch (e) {
        setError(e instanceof Error ? e.message : '학습내용 데이터를 저장하지 못했습니다.');
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [gradeNumber, workspaceId, scopeGradeId, subCategory, scopeTemplate]
  );

  return useMemo(
    () => ({
      rows,
      loading,
      saving,
      error,
      reload: load,
      setRows,
      saveRows,
    }),
    [error, load, loading, rows, saveRows, saving]
  );
}
